/**
 * @feature quote-requests
 * @layer presentation/components
 *
 * BudgetInfoCard - 3-Column Card (예산)
 * Displays budget amount and visibility status
 * Based on QuoteDetailPage design
 */

import { type FC } from "react";
import { DollarSign, CheckCircle2, AlertCircle } from "@semo/icons";
import { cn } from "../../../utils/cn";
import type { QuoteReviewCardProps } from "../types";
import { formatAmount } from "../utils/formatters";

/**
 * 3-Column Card: Budget Information
 * Shows:
 * - 희망 예산 (Budget amount)
 * - Visibility status (공개/비공개)
 */
export const BudgetInfoCard: FC<QuoteReviewCardProps> = ({ data, onEdit }) => {
  // Extract budget data
  const budgetAmount =
    typeof data.budgetAmount === "number" ? data.budgetAmount : undefined;

  // Budget visibility - check budgetType (Form) and budgetOption (API)
  const budgetType =
    typeof data.budgetType === "string" ? data.budgetType : undefined; // Form: 'public' | 'no_budget'
  const budgetOption =
    typeof data.budgetOption === "string" ? data.budgetOption : undefined; // API: 'public' | 'PUBLIC' | etc.

  // Determine budget visibility based on source
  const isBudgetPublic =
    budgetType === "public" ||
    budgetOption === "public" ||
    budgetOption === "PUBLIC";

  return (
    <div className={cn("bg-white", "rounded-[20px] p-5 sm:p-6")}>
      {/* Card Header */}
      <div className="flex items-center justify-between mb-5">
        <div className="flex items-center gap-3">
          {/* Icon */}
          <div className="w-9 h-9 rounded-[10px] bg-primary/5 flex items-center justify-center text-primary">
            <DollarSign className="w-4.5 h-4.5" />
          </div>
          {/* Title */}
          <h3 className="text-lg font-semibold text-gray-900 tracking-tight">
            예산
          </h3>
        </div>

        {/* Edit button - optional */}
        {onEdit && (
          <button
            type="button"
            onClick={onEdit}
            className={cn(
              "px-2 py-1",
              "text-xs font-semibold",
              "text-gray-400",
              "hover:text-primary transition-colors",
            )}
          >
            수정
          </button>
        )}
      </div>

      {/* Card Content */}
      <div className="space-y-4">
        {/* Budget Amount */}
        <div>
          <span className="text-2xs font-semibold text-gray-400 uppercase tracking-wider block mb-1">
            희망 예산
          </span>
          <span className="text-xl font-semibold text-primary tracking-tight">
            {formatAmount(budgetAmount)}
          </span>
        </div>

        {/* Visibility Status */}
        <div className="flex items-center gap-2 pt-3 border-t border-gray-50">
          {isBudgetPublic ? (
            <>
              <CheckCircle2 className="w-4 h-4 text-green-500" />
              <span className="text-xs font-semibold text-green-600">
                업체에 공개됨
              </span>
            </>
          ) : (
            <>
              <AlertCircle className="w-4 h-4 text-gray-300" />
              <span className="text-xs font-semibold text-gray-400">
                업체에 비공개
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
